import { onVariantUpdate, refreshOnVariantUpdate } from '@theme/pdp-utils';

/**
 * The Tabby / Tamara box of the Fandom product page.
 *
 * - The amounts in the box are printed by Liquid; `refreshOnVariantUpdate` morphs the box from the page Horizon fetched
 *   for the newly selected variant, so they always show that variant's price divided by 4.
 * - "Learn more" opens each provider's own popup by clicking the link inside the provider's widget (both widgets live in
 *   open shadow roots and are hidden by assets of blocks/pdp-bnpl.liquid). If the widget is not there, the link's own
 *   `href` opens the provider's public info page in a new tab.
 * - The hidden widgets are told the new amount so their popups match the selected variant.
 */

/**
 * Finds the first element matching `selector` inside any open shadow root below `root`.
 * @param {ParentNode} root
 * @param {string} selector
 * @returns {HTMLElement | null}
 */
function findInShadow(root, selector) {
  for (const element of root.querySelectorAll('*')) {
    const shadow = element.shadowRoot;
    if (!shadow) continue;
    const match = shadow.querySelector(selector) || findInShadow(shadow, selector);
    if (match) return /** @type {HTMLElement} */ (match);
  }
  return null;
}

/** @returns {boolean} True when Tabby's own popup was opened. */
function openTabby() {
  const root = document.querySelector('#TabbyPromo');
  const link = root && findInShadow(root, '[data-test="widget-learn-more"]');
  if (!link) return false;
  link.click();
  return true;
}

/** @returns {boolean} True when Tamara's own popup was opened. */
function openTamara() {
  const link = document
    .querySelector('tamara-widget#tamara-widget-custom, tamara-widget')
    ?.shadowRoot?.querySelector('.tamara-inline-learn-more-link');
  if (!link) return false;
  /** @type {HTMLElement} */ (link).click();
  return true;
}

class PdpBnpl extends HTMLElement {
  /** @type {(() => void)[]} */
  #cleanups = [];

  connectedCallback() {
    this.#cleanups.push(refreshOnVariantUpdate(this));
    this.#cleanups.push(onVariantUpdate(this, (event) => this.#syncProviders(event.detail?.resource)));
    this.addEventListener('click', this.#onClick);
    this.#hideProviderBlocks();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    for (const cleanup of this.#cleanups) cleanup();
    this.#cleanups = [];
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const link = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('a[data-bnpl-provider]') : null);
    if (!link) return;
    const opened = link.dataset.bnplProvider === 'tabby' ? openTabby() : openTamara();
    // Provider popup opened: stop the link. Otherwise the link opens the provider's info page in a new tab.
    if (opened) event.preventDefault();
  };

  /** The hidden provider blocks are duplicates of this box for screen readers. */
  #hideProviderBlocks() {
    document.querySelector('#TabbyPromo')?.closest('.shopify-app-block')?.setAttribute('aria-hidden', 'true');
    document.querySelector('.tamara-widget-block')?.setAttribute('aria-hidden', 'true');
  }

  /**
   * Gives the hidden provider widgets the selected variant's price so their popups match it.
   * @param {{ price?: number } | undefined} variant - The variant JSON from `variant:update`.
   */
  #syncProviders(variant) {
    if (typeof variant?.price !== 'number') return;
    const major = (variant.price / 100).toFixed(2);
    // @ts-ignore - global added by the Tabby app block
    window.TabbyPromoShopify?.refresh?.({ price: major });
    document.querySelector('tamara-widget#tamara-widget-custom')?.setAttribute('amount', major);
  }
}

if (!customElements.get('pdp-bnpl')) {
  customElements.define('pdp-bnpl', PdpBnpl);
}
